import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import type { ClientMeDto } from '@xcentral/api-client';
import { ApiError, useApi } from '../hooks/useApi';
import { isAccessDenied } from './accessDenied';

interface ClientTiersContextValue {
  tiers: string[];
  accountId: string | null;
  loading: boolean;
  /**
   * #19758 — the signed-in account is not a client-portal user at all: /client-it/me answered
   * 403/404, or Entra refused the API token (AADSTS50105). AuthLayout renders Access denied in
   * place of the shell. Never set by a 200 (even with tiers: []), by a transient failure, or while
   * loading.
   */
  noAccess: boolean;
  hasTier: (t: string) => boolean;
}

const DEFAULT_VALUE: ClientTiersContextValue = {
  tiers: [],
  accountId: null,
  loading: false,
  noAccess: false,
  hasTier: () => false,
};

/**
 * Exported so a test can supply a resolved tier set directly, exactly as `AccountContext` is used by
 * `pages/accessibility.test.tsx` — the provider above does its OWN `/client-it/me` fetch and needs
 * MSAL, which would add an unrelated queued response to every positional `mockApi.get` audit.
 * #16315 (the Licences request-a-licence-type control is ClientAdmin-only).
 */
export const ClientTiersContext = createContext<ClientTiersContextValue>(DEFAULT_VALUE);

/** The no-provider default: no tiers, so every tier-gated control is ABSENT rather than dead. */
export const DEFAULT_CLIENT_TIERS: ClientTiersContextValue = DEFAULT_VALUE;

export type { ClientTiersContextValue };

/**
 * #12629 (CPT-2) — fetches the caller's client-portal tiers from GET /api/v1/client-it/me
 * once per authenticated session. The ID token does not carry the tiers; this is the
 * single source of truth the SPA reads from.
 *
 * #19758 — this probe is the ONE place a 403 means "not a client-portal user": the endpoint takes
 * no account parameter, so the "not in scope for this account" 403 cannot arise here.
 *   200 (including tiers: [])       -> a client-portal user; tiers as returned. A real client
 *                                      whose roles map to no tiers gets 200, never 403.
 *   403 / 404, or AccessDeniedError -> noAccess (no client role, no PortalUsers row, or Entra
 *                                      refused the API token).
 *   anything else (5xx, network,    -> the previous behaviour: empty tiers, never noAccess — a
 *   abort, redirect in progress)       transient failure must not deny.
 * Other 403s (e.g. /child-accounts for ClientBilling/ClientUser) are NOT read here and stay normal.
 */
export function ClientTiersProvider({ children }: { children: ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const api = useApi();
  const [tiers, setTiers] = useState<string[]>([]);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [noAccess, setNoAccess] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setTiers([]);
      setAccountId(null);
      setNoAccess(false);
      setLoading(false);
      return;
    }

    let mounted = true;
    const ctrl = new AbortController();
    setLoading(true);
    setNoAccess(false);

    (async () => {
      try {
        const me = await api.get<ClientMeDto>('/api/v1/client-it/me', ctrl.signal);
        if (!mounted) return;
        setAccountId(me.accountId ?? null);
        setTiers(me.tiers ?? []);
      } catch (err) {
        if (!mounted) return;
        setTiers([]);
        setAccountId(null);
        // #19758 — only "no entitlement at all" denies. Everything else keeps the old
        // degrade-to-empty behaviour, so a transient failure never locks a real client out.
        const denied =
          isAccessDenied(err) || (err instanceof ApiError && (err.status === 403 || err.status === 404));
        setNoAccess(denied);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
      ctrl.abort();
    };
  }, [isAuthenticated, api]);

  const hasTier = (t: string) => tiers.includes(t);

  return (
    <ClientTiersContext.Provider value={{ tiers, accountId, loading, noAccess, hasTier }}>
      {children}
    </ClientTiersContext.Provider>
  );
}

export function useClientTiers() {
  return useContext(ClientTiersContext);
}
